"use client";

import Link from "next/link";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AppContext } from "@/context/AppContext";
import { useData, useLocalePath } from "@/i18n/LangContext";
import LanguageSwitch from "./LanguageSwitch";

const NAV_ITEMS = [
  { path: "/", label: "Home", page: "home", color: "#88ffff" },
  { path: "/about", label: "About", page: "about", color: "#88ff88" },
  { path: "/work", label: "Work", page: "work", color: "#ff88ff" },
  { path: "/blog", label: "Blog", page: "blog", color: "#7db8ff" },
  { path: "/contact", label: "Contact", page: "contact", color: "#ff8855" },
] as const;

export default function Navigation() {
  const { activePage, isOnCvSubdomain } = useContext(AppContext);
  const { ui } = useData();
  const lp = useLocalePath();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLElement>(null);

  // Diese Funktion fügt die Domain hinzu, wenn wir auf der CV-Subdomain sind
  const getHref = useCallback((path: string): string => {
    const localized = lp(path);
    if (isOnCvSubdomain) {
      return `https://robinwichmann.com${localized}`;
    }
    return localized;
  }, [lp, isOnCvSubdomain]);

  // ESC schließt; Fokus wandert ins Panel; Scroll-Lock solange offen
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [open]);

  return (
    <>
    <div className={`wrapper wrapper--top ${activePage}-page`}>
      <nav className="navigation w-full md:w-auto">
        <div className="navigation--frame">
          <div className="navigation--container">
            {/* Desktop: klassische Linkleiste */}
            <nav className="hidden md:block">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.page}
                  href={getHref(item.path)}
                  className={`navigation--link no-select ${activePage === item.page ? 'router-link-active' : ''}`}
                  style={activePage === item.page ? { color: item.color } : {}}
                >
                  {item.label}
                </Link>
              ))}
              {/* CV-Menüpunkt - nur auf der CV-Subdomain sichtbar */}
              {isOnCvSubdomain && (
                <Link
                  href="#"
                  className={`navigation--link no-select ${activePage === 'cv' ? 'router-link-active' : ''}`}
                  style={activePage === 'cv' ? { color: '#c69c6d' } : {}}
                  onClick={(e) => e.preventDefault()}
                >
                  CV
                </Link>
              )}
            </nav>

            {/* Mobil: Brand links, Burger rechts */}
            <div className="flex md:hidden items-center justify-between w-full px-4">
              <Link href={getHref("/")} className="navigation--brand no-select">
                <span className="navigation--brand-full">robinwichmann<span>.com</span></span>
                <span className="navigation--brand-short">rw</span>
              </Link>
              <button
                type="button"
                className={`navigation--burger no-select ${open ? 'is-open' : ''}`}
                aria-label={open ? ui.nav.close : ui.nav.open}
                aria-expanded={open}
                aria-controls="offcanvas-menu"
                onClick={() => setOpen(v => !v)}
              >
                <span /><span /><span />
              </button>
            </div>
          </div>
        </div>
      </nav>

    </div>

      {/* Offcanvas-Sidebar (nur mobil erreichbar) — bewusst AUSSERHALB von
          .wrapper: dessen filter/drop-shadow macht den Wrapper zum Containing
          Block für position:fixed und würde das Panel einsperren. */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              className="offcanvas--backdrop md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <motion.aside
              key="panel"
              id="offcanvas-menu"
              ref={panelRef}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-label={ui.nav.menu}
              className="offcanvas md:hidden"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 380, damping: 40 }}
            >
              <div className="offcanvas--head">
                <span className="offcanvas--eyebrow">{ui.nav.menu}</span>
                <button
                  type="button"
                  className="navigation--burger is-open no-select"
                  aria-label={ui.nav.close}
                  onClick={() => setOpen(false)}
                >
                  <span /><span /><span />
                </button>
              </div>

              <nav className="offcanvas--nav">
                {NAV_ITEMS.map((item, index) => (
                  <motion.div
                    key={item.page}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 + index * 0.05, duration: 0.3, ease: 'easeOut' }}
                  >
                    <Link
                      href={getHref(item.path)}
                      className={`offcanvas--link no-select ${activePage === item.page ? 'is-active' : ''}`}
                      style={{ '--link-accent': item.color } as React.CSSProperties}
                      onClick={() => setOpen(false)}
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                ))}
              </nav>

              <div className="offcanvas--foot" onClickCapture={(e) => { if ((e.target as HTMLElement).closest('a')) setOpen(false); }}>
                <LanguageSwitch size="lg" />
                <div className="offcanvas--legal">
                  <Link href={getHref("/imprint")} className="footer--link no-select text-sm">{ui.footer.imprint}</Link>
                  <Link href={getHref("/privacy")} className="footer--link no-select text-sm">{ui.footer.privacy}</Link>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
